import { isObservable } from "knockout";

import { useKoObservable } from "@services/useKoObservable";

import type { ExtractObservableValueOrRawValue, ReturnValueBasedOnType } from "./types";

/**
 * Given a value, return a useState-like tuple of [value, setValue].
 *
 * For 2 values like so:
 *
 * const obs = ko.observable(5);
 * const non = 5;
 *
 * the return of useKoObservableOrRawValue(obs) will be a tuple of type [number, (newValue: number | (oldValue: number) => number) => void]
 * the return of useKoObservableOrRawValue(non) will be a tuple of type [number, () => never]
 *
 * In the second case, the setter will throw if called, and will also trigger a TS build failure if an argument is passed.
 * This allows safely calling this hook with any value, a use case needed for extracting values from an observable of an object.
 * @deprecated please migrate away from knockout
 */
export function useKoObservableOrRawValue<T>(value: T): ReturnValueBasedOnType<T> {
  const returnValue = useKoObservable<ExtractObservableValueOrRawValue<T>>(isObservable(value) ? value : undefined);
  if (isObservable(value)) {
    return returnValue as ReturnValueBasedOnType<T>;
  }
  return [value, neverValueUpdater] as ReturnValueBasedOnType<T>;
}

function neverValueUpdater(): never {
  throw new Error("attempt to set a non-observable value");
}
